import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient.ts";
import { KnopClient } from "@opstel/query/knopClient.ts";
import { KnopHttpClient } from "@opstel/query/knopHttpClient.ts";
import { KlusModel, KlusModelArraySchema } from "@personeel/models/KlusModel";
import { KlusSoortenModel, KlusSoortenModelSchema } from "@personeel/models/KlusSoortModel";
import { LocatieModel, LocatieSchema } from "@personeel/models/LocatieModel";
import { PersoneelsdienstenModel } from "@personeel/models/PersoneelsdienstenModel.ts";
import {
  PersoneeldienstModelArraySchema,
  PersoneelsdienstModel,
} from "@personeel/models/PersoneelsdienstModel";
import {
  RangeerbewegingenModel,
  RangeerbewegingenSchemaArray,
} from "@personeel/models/RangeerBewegingModel";
import {
  PersoneelsinstellingenModel,
  PersoneelsinstellingenArraySchema,
} from "@personeel/models/PersoneelsinstellingenModel.ts";
import { useKlussenStore } from "@personeel/stores/KlussenStore";
import { useKlusSoortenStore } from "@personeel/stores/KlusSoortenStore";
import { usePersoneelsdienstenStore } from "@personeel/stores/PersoneelsdienstenStore";
import { useRangeerbewegingenStore } from "@personeel/stores/RangeerBewegingenStore";
import { sortAndGroupData } from "@personeel/utils/WerklijnenInputProcessing.ts";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { config } from "@shared/envVariables";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";
import { MuteerKlussenResultModel } from "@personeel/models/MuterenKlussenResultModel.ts";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore.ts";
import { Interval } from "luxon";

export interface PersoneelsdienstSchedulerJobResponse {
  ticket: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const getUrlPrefix = (): string => {
  return config.personeel.apiBaseUrl ?? "";
};

async function fetchDataWithTimeout<T>(
  url: string,
  knopClient: KnopClient,
  options?: RequestInit,
  timeoutMs?: number,
): Promise<T> {
  let controller: AbortController | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (typeof timeoutMs === "number") {
    controller = new AbortController();
    timeoutId = setTimeout(() => controller?.abort(), timeoutMs);
  }
  try {
    const token = await (knopClient as KnopHttpClient).getToken();
    if (!token) {
      throw new Error("Token is invalid");
    }

    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
      ...(controller ? { signal: controller.signal } : {}),
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return (await response.json()) as T;
    }
    return undefined as T; // voor void calls zoals delete
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error("Request timeout");
    }
    throw err;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export interface ValidationError extends Error {
  issues?: z.core.$ZodIssue[];
}

export function isValidationError(error: unknown): error is ValidationError {
  return (
    typeof error === "object" && error !== null && Array.isArray((error as ValidationError).issues)
  );
}

export function useFetchKlussenOverview() {
  const { setKlussen } = useKlussenStore();
  const instanceId = useCurrentInstanceId((state) => state.instanceId);
  const url = getUrlPrefix() + "/v1/klussen/" + instanceId;
  const { knopClient } = useKnopClient();
  const queryFn = async (): Promise<KlusModel[]> => {
    const data = await fetchDataWithTimeout<KlusModel[]>(url, knopClient, undefined, 5000);
    const validationResult = KlusModelArraySchema.safeParse(data);
    if (!validationResult.success) {
      const error = new Error("Validation failed: ") as ValidationError;
      error.issues = validationResult.error.issues;
      throw error;
    }
    return validationResult.data;
  };

  const result = useQuery<KlusModel[], Error>({
    queryKey: ["klussenOverview", instanceId],
    queryFn,
    enabled: instanceId != null,
    retry: false,
    // voor een scenario-id worden eenmalig de klussen opgehaald en in de datastore gezet, bij klussen maken, updaten, verwijderen wordt de datastore
    // bijgewerkt en niet nogmaals de klussen voor scenario-id opgehaald. Daarom staleTime: 0 en gcTime: 0
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (result.data) {
      setKlussen(result.data);
    }
  }, [result.data, setKlussen]);

  return { ...result };
}

export function useFetchKlussoorten() {
  const instanceId = useCurrentInstanceId((state) => state.instanceId);
  const setKlusSoorten = useKlusSoortenStore((s) => s.setKlusSoorten);
  const { knopClient } = useKnopClient();

  const url = getUrlPrefix() + "/v1/klussoorten";

  const queryFn = async (): Promise<KlusSoortenModel> => {
    const data = await fetchDataWithTimeout<KlusSoortenModel>(url, knopClient);
    const validationResult = KlusSoortenModelSchema.safeParse(data);
    if (!validationResult.success) {
      const error = new Error("Validation failed: ") as ValidationError;
      error.issues = validationResult.error.issues;
      throw error;
    }
    return validationResult.data;
  };

  const result = useQuery<KlusSoortenModel, Error>({
    queryKey: ["klussoorten", instanceId],
    queryFn,
    enabled: instanceId != null,
    retry: false,
    // cache blijft intact totdat applicatie opnieuw gestart wordt, bij scenariowissel worden
    // de gegevens uit de cache met key ["klussoorten", scenarioId] gehaald als deze cache er is.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (result.data) {
      setKlusSoorten(result.data);
    }
  }, [result.data, setKlusSoorten]);

  return { ...result };
}

export function useFetchPersoneelsdiensten() {
  const instanceId = useCurrentInstanceId((state) => state.instanceId);
  const setPersoneelsdiensten = usePersoneelsdienstenStore((s) => s.setPersoneelsdiensten);

  const url = getUrlPrefix() + "/v1/personeelsdiensten/" + instanceId;
  const { knopClient } = useKnopClient();

  const queryFn = async (): Promise<PersoneelsdienstenModel[]> => {
    const data = await fetchDataWithTimeout<PersoneelsdienstModel[]>(url, knopClient);
    const validationResult = PersoneeldienstModelArraySchema.safeParse(data);
    if (!validationResult.success) {
      const error = new Error("Validation failed: ") as ValidationError;
      error.issues = validationResult.error.issues;
      throw error;
    }
    return sortAndGroupData(validationResult.data);
  };

  const result = useQuery<PersoneelsdienstenModel[], Error>({
    queryKey: ["personeelsdiensten", instanceId],
    queryFn,
    enabled: instanceId != null,
    retry: false,
    // cache blijft intact totdat applicatie opnieuw gestart word, bij scenariowissel worden
    // de gegevens uit de cache met key ["personeelsdiensten", scenarioId] gehaald als deze cache er is.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (result.data) {
      setPersoneelsdiensten(result.data);
    }
  }, [result.data, setPersoneelsdiensten]);

  return { ...result };
}

export function useFetchLocaties() {
  const instanceId = useCurrentInstanceId((state) => state.instanceId);

  const url = getUrlPrefix() + "/v1/locaties/" + instanceId;
  const { knopClient } = useKnopClient();

  const queryFn = async (): Promise<LocatieModel> => {
    const data = await fetchDataWithTimeout<LocatieModel>(url, knopClient);
    const validationResult = LocatieSchema.safeParse(data);
    if (!validationResult.success) {
      const error = new Error("Validation failed: ") as ValidationError;
      error.issues = validationResult.error.issues;
      throw error;
    }
    return validationResult.data;
  };

  const result = useQuery<LocatieModel, Error>({
    queryKey: ["locaties", instanceId],
    queryFn,
    enabled: instanceId != null,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return { ...result };
}

export function useFetchRangeerbewegingen() {
  const instanceId = useCurrentInstanceId((state) => state.instanceId);
  const setRangeerbewegingen = useRangeerbewegingenStore((s) => s.setRangeerbewegingen);
  const { knopClient } = useKnopClient();
  const url = getUrlPrefix() + "/v1/rangeerbewegingen/" + instanceId;

  const queryFn = async (): Promise<RangeerbewegingenModel> => {
    const data = await fetchDataWithTimeout<RangeerbewegingenModel>(url, knopClient);
    const validationResult = RangeerbewegingenSchemaArray.safeParse(data);
    if (!validationResult.success) {
      const error = new Error("Validation failed: ") as ValidationError;
      error.issues = validationResult.error.issues;
      throw error;
    }
    return validationResult.data;
  };

  const result = useQuery<RangeerbewegingenModel, Error>({
    queryKey: ["rangeerbewegingen", instanceId],
    queryFn,
    enabled: instanceId != null,
    retry: 3, // retry omdat hier af en toe 'net::ERR_ABORTED 400 (Bad Request)' tevoorschijn komt
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (result.data) {
      setRangeerbewegingen(result.data);
    }
  }, [result.data, setRangeerbewegingen]);

  return { ...result };
}

const deleteKlus = async (
  klusId: number,
  knopClient: KnopClient,
): Promise<MuteerKlussenResultModel> => {
  return fetchDataWithTimeout<MuteerKlussenResultModel>(
    getUrlPrefix() + "/v1/klus/" + klusId,
    knopClient,
    {
      method: "DELETE",
    },
  );
};

export const useDeleteKlus = () => {
  const setKlussenOverviewItems = useKlussenStore((s) => s.setKlussen);
  const { knopClient } = useKnopClient();

  return useMutation({
    mutationFn: async (klusId: number) => {
      return deleteKlus(klusId, knopClient);
    },
    onSuccess: (result) => {
      const alleGewijzigdeKlussen = KlusModelArraySchema.parse(result.gemuteerdeKlussen ?? []);
      const verwijderdeIds = new Set(result.verwijderdeKlussen ?? []);
      const gewijzigdeIds = new Set(alleGewijzigdeKlussen.map((k) => Number(k.id)));

      setKlussenOverviewItems((prev) =>
        [
          ...prev.filter(
            (k) => !verwijderdeIds.has(Number(k.id)) && !gewijzigdeIds.has(Number(k.id)),
          ),
          ...alleGewijzigdeKlussen,
        ].sort((a, b) => a.id - b.id),
      );
    },
  });
};

const addKlus = async (
  klus: Partial<KlusModel>,
  knopClient: KnopClient,
): Promise<MuteerKlussenResultModel> => {
  return fetchDataWithTimeout<MuteerKlussenResultModel>(getUrlPrefix() + "/v1/klus", knopClient, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(klus),
  });
};

export const useAddOrModifyKlus = () => {
  const instanceId = useCurrentInstanceId((state) => state.instanceId);
  const setKlussenOverviewItems = useKlussenStore((s) => s.setKlussen);
  const { knopClient } = useKnopClient();

  return useMutation({
    mutationFn: async (klus: Partial<KlusModel>) => {
      if (klus.id === -1) delete klus.id;
      klus.scenarioId = instanceId!;
      return addKlus(klus, knopClient);
    },
    onSuccess: (result) => {
      const alleGewijzigdeKlussen = KlusModelArraySchema.parse(result.gemuteerdeKlussen ?? []);

      const verwijderdeIds = new Set(result.verwijderdeKlussen ?? []);
      const gewijzigdeIds = new Set(alleGewijzigdeKlussen.map((k) => Number(k.id)));

      setKlussenOverviewItems((prev) =>
        [
          ...prev.filter(
            (k) => !verwijderdeIds.has(Number(k.id)) && !gewijzigdeIds.has(Number(k.id)),
          ),
          ...alleGewijzigdeKlussen,
        ].sort((a, b) => a.id - b.id),
      );
    },
  });
};

export function useKlussenGenerator() {
  const instanceId = useCurrentInstanceId((state) => state.instanceId);
  const setKlussenOverviewItems = useKlussenStore((s) => s.setKlussen);
  const { knopClient } = useKnopClient();

  const getUrl = (startDatum: string, eindDatum: string) => {
    return (
      getUrlPrefix() +
      "/v1/genereer-klussen?scenarioId=" +
      instanceId +
      "&beginDatumtijd=" +
      encodeURIComponent(startDatum) +
      "&eindDatumtijd=" +
      encodeURIComponent(eindDatum)
    );
  };

  return useMutation({
    mutationFn: async ({ startDatum, eindDatum }: { startDatum: string; eindDatum: string }) => {
      const data: MuteerKlussenResultModel = await fetchDataWithTimeout(
        getUrl(startDatum, eindDatum),
        knopClient,
      );

      const validationResult = KlusModelArraySchema.safeParse(data.gemuteerdeKlussen ?? []);
      if (!validationResult.success) {
        const error = new Error("Validation failed: ") as ValidationError;
        error.issues = validationResult.error.issues;
        throw error;
      }
      const alleGewijzigdeKlussen = KlusModelArraySchema.parse(data.gemuteerdeKlussen ?? []);
      const verwijderdeIds = new Set(data.verwijderdeKlussen ?? []);
      const gewijzigdeIds = new Set(alleGewijzigdeKlussen.map((k) => Number(k.id)));

      setKlussenOverviewItems((prev) =>
        [
          ...prev.filter(
            (k) => !verwijderdeIds.has(Number(k.id)) && !gewijzigdeIds.has(Number(k.id)),
          ),
          ...alleGewijzigdeKlussen,
        ].sort((a, b) => a.id - b.id),
      );
      return data.gemuteerdeKlussen;
    },
    retry: 0,
  });
}

const executePersoneelsdienstScheduler = async (
  payload: {
    start: string;
    eind: string;
    dienstIds: string[];
  },
  knopClient: KnopClient,
  instanceId: string,
  scenarioInterval: Interval,
): Promise<PersoneelsdienstSchedulerJobResponse> => {
  const url = getUrlPrefix() + "/v1/ps-jobs";
  return await fetchDataWithTimeout<PersoneelsdienstSchedulerJobResponse>(url, knopClient, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      scenarioId: instanceId,
      beginDatumtijd: payload.start,
      eindDatumtijd: payload.eind,
      dienstIds: payload.dienstIds,
      scenarioBeginDatumtijd: scenarioInterval.start,
      scenarioEindDatumtijd: scenarioInterval.end,
    }),
  });
};

export const usePersoneelsdienstScheduler = () => {
  const instanceId = useCurrentInstanceId((state) => state.instanceId);
  const { knopClient } = useKnopClient();
  const { scenarioInterval } = useSelectedTimeStore.getState();

  return useMutation({
    mutationFn: async (payload: { start: string; eind: string; dienstIds: string[] }) => {
      if (!instanceId) {
        throw new Error("Instance ID is required");
      }
      if (!scenarioInterval) {
        throw new Error("Scenario interval is required");
      }

      return await executePersoneelsdienstScheduler(
        payload,
        knopClient,
        instanceId,
        scenarioInterval,
      );
    },
    retry: 0,
  });
};

export function useFetchPersoneelsinstellingen(
  vanafTijd: string,
  totEnMetTijd: string,
  knooppuntNaam: string,
) {
  const { knopClient } = useKnopClient();

  const queryKey = ["personeelsinstellingen", vanafTijd, totEnMetTijd, knooppuntNaam];
  const getUrl = () => {
    const baseUrl =
      getUrlPrefix() +
      `/v1/personeelsinstellingen/${encodeURIComponent(vanafTijd)}/${encodeURIComponent(totEnMetTijd)}`;
    const params = new URLSearchParams();
    params.append("knooppuntNaam", knooppuntNaam);
    return baseUrl + (params.toString() ? `?${params.toString()}` : "");
  };

  const queryFn = async (): Promise<PersoneelsinstellingenModel[]> => {
    const data = await fetchDataWithTimeout<PersoneelsinstellingenModel[]>(getUrl(), knopClient);
    const validationResult = PersoneelsinstellingenArraySchema.safeParse(data);
    if (!validationResult.success) {
      const error = new Error("Validation failed: ") as ValidationError;
      error.issues = validationResult.error.issues;
      throw error;
    }
    return validationResult.data;
  };

  return useQuery<PersoneelsinstellingenModel[], Error>({
    queryKey,
    queryFn,
    enabled: !!vanafTijd && !!totEnMetTijd && !!knooppuntNaam,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
